import React, { useEffect, useState } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Input, 
  Button, 
  Form, 
  Modal, 
  Typography, 
  Spin, 
  Alert,
  Select 
} from 'antd';
import Setting from '../Setting';
import { 
  HeartOutlined, 
  PlusOutlined, 
  UserOutlined,
} from '@ant-design/icons';
import { RequestAPI } from '../backend/api';
import { useAuth } from '../utils/auth';

const { Title, Paragraph } = Typography;
const { Option } = Select;

const RequestPage = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [genres, setGenres] = useState([]);

  useEffect(() => {
    fetchRequests();
    fetchGenres();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const extractRequests = (res) => {
    const data = res?.data;
    if (Array.isArray(data)) return data;
    if (data?.items && Array.isArray(data.items)) return data.items;
    if (data?.data && Array.isArray(data.data)) return data.data;
    return [];
  };

  const fetchRequests = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await RequestAPI.getRequests(1, 50); // Get first 50 requests
      setRequests(extractRequests(response));
    } catch (err) {
      setError('Failed to fetch requests');
      console.error('Requests fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchGenres = async () => {
    try {
      const response = await fetch('/api/genres');
      const data = await response.json();
      setGenres(Array.isArray(data) ? data : []);
    } catch (error) {
      setGenres([]);
    }
  };

  const handleSubmit = async (values) => {
    try {
      await RequestAPI.createRequest(values);
      Setting.showMessage('success', 'Request submitted successfully!');
      setIsModalVisible(false);
      form.resetFields();
      fetchRequests();
    } catch (error) {
      Setting.showMessage('error', 'Failed to submit request');
    }
  };

  const handleUpvote = async (requestId) => {
    try {
      await RequestAPI.upvoteRequest(requestId);
      Setting.showMessage('success', 'Upvote added!');
      fetchRequests();
    } catch (error) {
      Setting.showMessage('error', 'Failed to upvote');
    }
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
        style={{ margin: '20px' }}
      />
    );
  }

  const sortedRequests = [...requests].sort((a, b) => b.upvotes - a.upvotes);

  return (
    <div>
      <Title level={2}>Movie Requests</Title>
      <Paragraph>
        Can't find what you're looking for? Request it here! 
        {user ? (
          <Button type="primary" onClick={() => setIsModalVisible(true)} style={{ marginLeft: '20px' }}>
            <PlusOutlined /> New Request
          </Button>
        ) : (
          <span> Please sign in to submit requests.</span>
        )}
      </Paragraph>

      <Modal
        title="Submit Movie Request"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
      >
        <Form
          form={form}
          onFinish={handleSubmit}
          layout="vertical"
        >
          <Form.Item
            name="title"
            label="Movie Title"
            rules={[{ required: true, message: 'Please enter the movie title' }]}
          >
            <Input placeholder="e.g. Inception 2" />
          </Form.Item>

          <Form.Item
            name="year"
            label="Release Year"
            rules={[{ required: true, message: 'Please enter the release year' }]}
          >
            <Input type="number" placeholder="e.g. 2025" />
          </Form.Item>

          <Form.Item
            name="genre"
            label="Genre"
            rules={[{ required: true, message: 'Please select a genre' }]}
          >
            <Select placeholder="Select genre">
              {genres.map((genre) => (
                <Option key={genre} value={genre}>{genre}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="reason"
            label="Why should we add this?"
            rules={[{ required: true, message: 'Please explain why you want this movie' }]}
          >
            <Input.TextArea rows={3} placeholder="e.g. This is a highly anticipated sequel..." />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit">
              Submit Request
            </Button>
            <Button onClick={() => setIsModalVisible(false)} style={{ marginLeft: '10px' }}>
              Cancel
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Row gutter={[16, 16]}>
        {sortedRequests.map((request) => (
          <Col key={request.id} xs={24} sm={12} md={8} lg={6}>
            <Card
              actions={[
                <HeartOutlined key="upvote" style={{ fontSize: '18px', color: '#eb2f96' }} />,
                <UserOutlined key="user" />,
              ]}
            >
              <Card.Meta
                title={request.title}
                description={`Year: ${request.year} • Genre: ${request.genre}`}
              />
              <Paragraph style={{ textAlign: 'center', marginTop: '10px' }}>
                <strong>{request.upvotes}</strong> upvotes
              </Paragraph>
              <Paragraph style={{ textAlign: 'center' }}>
                {request.reason.substring(0, 100)}...
              </Paragraph>
              {user && (
                <Button 
                  type="primary" 
                  size="small" 
                  onClick={() => handleUpvote(request.id)}
                  style={{ width: '100%' }}
                >
                  Upvote
                </Button>
              )}
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default RequestPage;